/**
 * County/City Permit Types Configuration
 * 
 * This configuration maps counties/cities to their available permit types.
 * Each permit type includes:
 * - id: unique identifier for routing
 * - name: display name
 * - description: user-friendly description
 * - icon: emoji or icon identifier
 * - route: the route path for the form component
 */

// Permit type definitions (reusable across counties)
const PERMIT_TYPE_DEFINITIONS = {
  building: {
    id: 'building',
    name: 'Building Permit',
    description: 'For construction, renovation, and improvement projects',
    icon: 'BLD',
    route: '/submit/building'
  },
  row: {
    id: 'row',
    name: 'Right of Way (ROW) Permit',
    description: 'For work in public streets, sidewalks, and trails',
    icon: 'ROW',
    route: '/submit/row'
  },

  // Indianapolis - Class 2 (Residential) permits
  indy_res_class2: {
    id: 'indy_res_class2',
    name: 'Residential Development Permit (Class 2)',
    description: 'Class 2 residential permits in Indianapolis (Development, HVAC, Plumbing, Wrecking)',
    icon: 'RES',
    route: '/submit/indy/residential'
  },

  // Internal sub-permits for Indy Class 2 (used by the second-step selector)
  indy_res_dev: {
    id: 'indy_res_dev',
    name: 'Residential Development / Structural',
    description: 'Development / structural permit for Class 2 residential projects in Indianapolis',
    icon: 'RES',
    route: '/submit/indy/residential-development'
  },
  indy_res_hvac: {
    id: 'indy_res_hvac',
    name: 'Residential Heating & Cooling Permit',
    description: 'HVAC permits for Class 2 residential projects in Indianapolis',
    icon: 'HVAC',
    route: '/submit/indy/residential-hvac'
  },
  indy_res_plumbing: {
    id: 'indy_res_plumbing',
    name: 'Residential Plumbing Permit',
    description: 'Plumbing permits for Class 2 residential projects in Indianapolis',
    icon: 'PLMB',
    route: '/submit/indy/residential-plumbing'
  },
  indy_res_wrecking: {
    id: 'indy_res_wrecking',
    name: 'Residential Wrecking / Removal Permit',
    description: 'Demolition/removal permits for Class 2 residential structures in Indianapolis',
    icon: 'WRCK',
    route: '/submit/indy/residential-wrecking'
  }
  // Future permit types can be added here:
  // sewer: {
  //   id: 'sewer',
  //   name: 'Sewer Tap Permit',
  //   description: 'For sewer connection applications',
  //   icon: 'PLMB',
  //   route: '/submit/sewer'
  // }
};

/**
 * County/City Permit Types Mapping
 * 
 * Maps county names (or city+county combinations) to their available permit types.
 * Format: "County Name" or "City, County Name"
 */
export const COUNTY_PERMIT_TYPES = {
  // West Lafayette, Tippecanoe County
  'Tippecanoe County': ['building', 'row'],
  'West Lafayette': ['building', 'row'], // Also support city name lookup
  
  // Indianapolis, Marion County - Class 2 (Residential) entry point only (demo scope)
  'Marion County': ['indy_res_class2'],
  'Indianapolis, Marion County': ['indy_res_class2'],

  // Kokomo, Howard County (future)
  'Howard County': ['building', 'row'], // Example: Kokomo might have same types initially
  'Kokomo': ['building', 'row'],
  
  // Default fallback - if county not found, show all available types
  'default': ['building', 'row']
};

/**
 * Get available permit types for a given county/city
 * @param {string} county - County name
 * @param {string} city - City name (optional, for more specific matching)
 * @returns {Array} Array of permit type objects
 */
export const getPermitTypesForLocation = (county, city = null) => {
  // Try city first (more specific)
  let permitTypeIds = COUNTY_PERMIT_TYPES[city] || 
                      COUNTY_PERMIT_TYPES[county] || 
                      COUNTY_PERMIT_TYPES['default'];
  
  // Return full permit type objects
  return permitTypeIds
    .map(id => PERMIT_TYPE_DEFINITIONS[id])
    .filter(type => type !== undefined); // Filter out undefined if config is missing
};

/**
 * Get all permit type definitions
 * @returns {Object} All permit type definitions
 */
export const getAllPermitTypeDefinitions = () => {
  return PERMIT_TYPE_DEFINITIONS;
};

const permitTypesConfig = {
  COUNTY_PERMIT_TYPES,
  PERMIT_TYPE_DEFINITIONS,
  getPermitTypesForLocation,
  getAllPermitTypeDefinitions
};

export default permitTypesConfig;
